import type { RiskFlowAlert } from "./riskflow-feed";

export type RiskFlowSourceFilter = "Wire" | "OSINT";
export type RiskFlowSortMode = "latest" | "priority" | "impact";

export const RISKFLOW_SOURCE_FILTERS: RiskFlowSourceFilter[] = [
  "Wire",
  "OSINT",
];

export const RISKFLOW_SORT_MODES: {
  value: RiskFlowSortMode;
  label: string;
}[] = [
  { value: "latest", label: "Latest" },
  { value: "priority", label: "Priority" },
  { value: "impact", label: "Impact" },
];

function sourceKey(value: string | null | undefined): string {
  return (value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function normalizeRiskFlowSourceSelection(
  values: readonly unknown[] | null | undefined,
): Set<RiskFlowSourceFilter> {
  const next = new Set<RiskFlowSourceFilter>();
  for (const value of values ?? []) {
    if (value === "Wire" || value === "OSINT") next.add(value);
  }
  return next.size > 0 ? next : new Set(RISKFLOW_SOURCE_FILTERS);
}

export function riskFlowDisplayCategory(
  source: string | null | undefined,
  category?: string | null,
): RiskFlowSourceFilter {
  const categoryKey = sourceKey(category);
  if (categoryKey === "osint") return "OSINT";
  if (categoryKey === "wire") return "Wire";

  const key = sourceKey(source);
  if (key.includes("osint") || key === "osint613rss") return "OSINT";
  return "Wire";
}

export function riskFlowSourceOfAlert(
  alert: Pick<RiskFlowAlert, "source" | "category">,
): RiskFlowSourceFilter {
  return riskFlowDisplayCategory(alert.source, alert.category);
}

export function matchesRiskFlowSources(
  alert: Pick<RiskFlowAlert, "source" | "category">,
  selected: Set<RiskFlowSourceFilter>,
): boolean {
  const safeSelected =
    selected.size > 0 ? selected : new Set(RISKFLOW_SOURCE_FILTERS);
  return safeSelected.has(riskFlowSourceOfAlert(alert));
}

export function countRiskFlowSources(
  alerts: Array<Pick<RiskFlowAlert, "source" | "category">>,
): Partial<Record<RiskFlowSourceFilter, number>> {
  return alerts.reduce<Partial<Record<RiskFlowSourceFilter, number>>>(
    (counts, alert) => {
      const source = riskFlowSourceOfAlert(alert);
      counts[source] = (counts[source] ?? 0) + 1;
      return counts;
    },
    {},
  );
}

function publishedTime(alert: RiskFlowAlert): number {
  const time = Date.parse(alert.publishedAt);
  return Number.isFinite(time) ? time : 0;
}

function severityRank(alert: RiskFlowAlert): number {
  if (alert.severity === "critical") return 4;
  if (alert.severity === "high") return 3;
  if (alert.severity === "medium") return 2;
  return 1;
}

function impactRank(alert: RiskFlowAlert): number {
  return alert.ivScore ?? alert.headlineWeight ?? alert.pointRange ?? 0;
}

export function sortRiskFlowAlerts(
  alerts: RiskFlowAlert[],
  mode: RiskFlowSortMode,
): RiskFlowAlert[] {
  const next = [...alerts];
  next.sort((a, b) => {
    if (mode === "priority") {
      return (
        severityRank(b) - severityRank(a) ||
        impactRank(b) - impactRank(a) ||
        publishedTime(b) - publishedTime(a)
      );
    }
    if (mode === "impact") {
      return (
        impactRank(b) - impactRank(a) ||
        severityRank(b) - severityRank(a) ||
        publishedTime(b) - publishedTime(a)
      );
    }
    return publishedTime(b) - publishedTime(a);
  });
  return next;
}
